import { HttpErrorResponse } from '@angular/common/http';

import { TrackingActivity } from '@recruitee/http/types';
import {
  createEntityCollectionStore,
  DEFAULT_SCOPE_NAME,
  memoizeSelector,
  pipeState,
} from '@recruitee/ngrx';

import { getStoreScope } from '../../helpers/store-helper';
import {
  ActivitiesDataScope,
  EntityStoreKey,
  TrackingActivitiesFiltersParams,
  TrackingActivitiesStore,
} from '../../types';

const {
  actions,
  selectors: defaultSelectors,
  reducer,
  namespace,
} = createEntityCollectionStore<TrackingActivity>()(EntityStoreKey.trackingActivities, {
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => ({
    fetchCollection: (
      state,
      payload: { params: TrackingActivitiesFiltersParams; scope?: string; replace?: boolean },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.resetAll(next) : next),
        next =>
          adapter.markAsPending(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: [],
          }),
      ),
    fetchCollectionSuccess: (
      state,
      payload: { data: TrackingActivitiesStore; scope?: string; replace?: boolean },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
        next => adapter.addMany(next, payload.data.activities, { scope: payload.scope }),
        next =>
          adapter.markAsLoaded(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: payload.data.activities.map(e => e.id),
          }),
      ),

    fetchCandidateTrackingActivities: (
      state,
      payload: {
        params: TrackingActivitiesFiltersParams;
        scope?: string;
        replace?: boolean;
        loadMore?: boolean;
      },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.resetAll(next) : next),
        next =>
          adapter.markAsPending(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: [],
          }),
      ),
    fetchCandidateTrackingActivitiesSuccess: (
      state,
      payload: {
        data: TrackingActivitiesStore;
        scope?: string;
        replace?: boolean;
        loadMore?: boolean;
      },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
        next =>
          adapter.addMany(next, payload.data.activities, {
            scope: payload.scope,
            prepend: !payload.loadMore,
          }),
        next =>
          adapter.markAsLoaded(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: payload.data.activities.map(e => e.id),
          }),
      ),
    fetchCandidateTrackingActivitiesFailed: (state, payload: { error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error),

    fetchOfferTrackingActivities: (
      state,
      payload: { params: TrackingActivitiesFiltersParams; scope?: string; replace?: boolean },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.resetAll(next) : next),
        next =>
          adapter.markAsPending(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: [],
          }),
      ),
    fetchOfferTrackingActivitiesSuccess: (
      state,
      payload: { data: TrackingActivitiesStore; scope?: string; replace?: boolean },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
        next => adapter.addMany(next, payload.data.activities, { scope: payload.scope }),
        next =>
          adapter.markAsLoaded(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: payload.data.activities.map(e => e.id),
          }),
      ),
    fetchOfferTrackingActivitiesFailed: (state, payload: { error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error),
    fetchRequisitionTrackingActivities: (
      state,
      payload: { params: TrackingActivitiesFiltersParams; scope?: string; replace?: boolean },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.resetAll(next) : next),
        next =>
          adapter.markAsPending(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: [],
          }),
      ),
    fetchRequisitionTrackingActivitiesSuccess: (
      state,
      payload: { data: TrackingActivitiesStore; scope?: string; replace?: boolean },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
        next => adapter.addMany(next, payload.data.activities, { scope: payload.scope }),
        next =>
          adapter.markAsLoaded(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: payload.data.activities.map(e => e.id),
          }),
      ),
    fetchRequisitionTrackingActivitiesFailed: (state, payload: { error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error),
  }),
});

const selectors = {
  ...defaultSelectors,
  requisitionScopeIsLoaded: memoizeSelector((requisitionId: number) =>
    defaultSelectors.isLoaded({
      scope: getStoreScope(ActivitiesDataScope.Requisition, requisitionId),
    }),
  ),
};

export {
  actions as TrackingActivitiesActions,
  selectors as TrackingActivitiesSelectors,
  reducer as TrackingActivitiesReducer,
  namespace as TrackingActivitiesNamespace,
};
